import React from 'react';
import { Subject, ClickoutSegment, isBrowser } from '@packages/common';
import { ABTestingContext, AppStaticStateContext } from '../../context';
import { RecommendationChannel } from '../../containers';
import { ImpressionTrackerProvider, ConditionPayload } from '../../context/ImpressionTrackerContext';

export interface ProductCarouselImpressionTrackerProps {
  recommendationChannel?: RecommendationChannel;
  onAfterSlideSubject: Subject<number[]>;
  categoryId?: string;
  isSorted?: boolean;
}

export const ProductCarouselImpressionTracker: React.FC<ProductCarouselImpressionTrackerProps> = ({
  children,
  categoryId,
  recommendationChannel,
  isSorted,
  onAfterSlideSubject,
}) => {
  const { deviceType } = React.useContext(AppStaticStateContext);
  const impressionsSubject = React.useRef<Subject<boolean>>(new Subject<boolean>(false));
  const impressionTrackedIndexes = React.useRef<number[]>([]);
  const visibleIndexes = React.useRef<number[]>([]);
  const {
    config: { SORTING_INDICE },
  } = React.useContext(ABTestingContext);

  // Reset values if user-side navigation happens.
  // Otherwise recommendations will not fire if slider of other category was seen already.
  // Use pathname, as categoryId is optional
  const windowLocation = isBrowser() ? window.location.pathname : '';
  React.useEffect(() => {
    visibleIndexes.current = [];
    impressionTrackedIndexes.current = [];
  }, [windowLocation]);

  React.useEffect(() => {
    const sub = onAfterSlideSubject.subscribe((indexes: number[]) => {
      visibleIndexes.current = indexes;
      impressionsSubject.current.next(true);
    });
    return () => {
      sub.unsubscribe();
    };
  }, [onAfterSlideSubject]);

  const condition = React.useCallback(
    ({ index }: ConditionPayload) => {
      const isVisible = visibleIndexes.current.includes(index) && !impressionTrackedIndexes.current.includes(index);
      // Prevent additional calls for off-screen object clones until next reccommendations fetched
      if (isVisible) {
        impressionTrackedIndexes.current.push(index);
      }
      return isVisible;
    },
    [visibleIndexes, impressionTrackedIndexes],
  );

  const impressionMetadata = React.useMemo(
    () => ({
      categoryId: categoryId ? categoryId : 0,
      recommendationChannel,
      deviceType,
      segment: ClickoutSegment.RECOMMENDATION,
      sortedList: !!isSorted,
      trackingIndice: SORTING_INDICE || '',
    }),
    [categoryId, recommendationChannel, deviceType, isSorted, SORTING_INDICE],
  );

  return (
    <ImpressionTrackerProvider
      containerSelector={'[data-testid="carousel-stage"]'}
      manualTriggerSubject={impressionsSubject.current}
      condition={condition}
      impressionMetadata={impressionMetadata}
    >
      {children}
    </ImpressionTrackerProvider>
  );
};
